import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const Privacy = () => {
  const privacyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - Digital Shark",
    "description": "Privacy policy for Digital Shark Inc., outlining how we collect, use, and protect your personal information.",
    "url": "https://digitalshark.tv/privacy",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Digital Shark",
      "url": "https://digitalshark.tv"
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Privacy Policy"
        description="Privacy policy for Digital Shark Inc., outlining how we collect, use, and protect your personal information."
        keywords="privacy policy, Digital Shark privacy, data protection, personal information, GDPR compliance, Caribbean privacy policy"
        url="/privacy"
        structuredData={privacyStructuredData}
      />
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 to-primary-800 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300">
              How we collect, use, and protect your personal information
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="prose prose-lg max-w-none"
            >
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-primary-800 mb-6">Information We Collect</h2>
                <p className="text-primary-600 mb-4">
                  Digital Shark Inc. we collect information you provide directly to us, such as when you:
                </p>
                
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>Contact us through our website or email</li>
                  <li>Request our services or quotes</li>
                  <li>Subscribe to our newsletter or updates</li>
                  <li>Participate in surveys or feedback forms</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">How We Use Your Information</h2>
                <p className="text-primary-600 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Send you technical notices, updates, and promotional materials</li>
                  <li>Process transactions and send related information</li>
                  <li>Comply with legal obligations</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Information Sharing</h2>
                <p className="text-primary-600 mb-4">
                  We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except:
                </p>
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>To trusted service providers who assist us in operating our website and conducting our business</li>
                  <li>When required by law or to protect our rights and safety</li>
                  <li>In connection with a business transfer or acquisition</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Data Security</h2>
                <p className="text-primary-600 mb-6">
                  We implement appropriate security measures to protect your personal information against unauthorized access, 
                  alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Cookies and Tracking</h2>
                <p className="text-primary-600 mb-6">
                  Our website may use cookies and similar tracking technologies to enhance your browsing experience. 
                  You can control cookie settings through your browser preferences.
                </p>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Your Rights</h2>
                <p className="text-primary-600 mb-4">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-primary-600 mb-6 space-y-2">
                  <li>Access and update your personal information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent for data processing</li>
                </ul>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Contact Us</h2>
                <p className="text-primary-600 mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-primary-600 mb-2">
                    <strong>Email:</strong> admin@digitalshark.tv
                  </p>
                  <p className="text-primary-600 mb-2">
                    <strong>Phone:</strong> +1-265-250-0424
                  </p>
                  <p className="text-primary-600">
                    <strong>Address:</strong> French Village, St. Peter, Barbados
                  </p>
                </div>

                <h2 className="text-2xl font-bold text-primary-800 mb-6">Changes to This Policy</h2>
                <p className="text-primary-600">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                  the new Privacy Policy on this page and updating the "Last updated" date.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
